import React, { useMemo, useState, useCallback } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { utils, writeFile } from 'xlsx';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const DemographicAnalysis = ({ conversations }) => {
  const [selectedState, setSelectedState] = useState('');
  // New state for dropdown selected municipio display
  const [selectedMunicipalityState, setSelectedMunicipalityState] = useState('');

  const demoData = useMemo(() => {
    const locationCounts = { states: new Map(), cities: new Map() };
    const ageCounts = new Map();
    const processedConvIds = new Set();

    const postalCodeRegex = /- Código postal: \d+ \(([^,]+), ([^\)]+)\)/;
    const ageRegex = /- Edad: (\d+) años/;
    // We already exclude this in the code, but added check in data return as well
    const genderQuestionText = 'por favor, indícame tu género (Masculino o Femenino)';

    conversations.forEach(conv => {
      if (!conv.messages || processedConvIds.has(conv.id)) return;

      let locationFound = false;
      let ageFound = false;

      for (let i = conv.messages.length - 1; i >= 0; i--) {
        const message = conv.messages[i];
        if (message.role !== 'assistant' || typeof message.content !== 'string') continue;

        if (message.content.includes(genderQuestionText)) continue;

        if (!locationFound) {
          const locationMatch = message.content.match(postalCodeRegex);
          if (locationMatch) {
            const city = locationMatch[1].trim();
            let state = locationMatch[2].trim();

            if (state === 'México') {
              state = 'Estado de México';
            }
            // Exclude if state is exactly the gender question text to avoid false positives
            if (state !== genderQuestionText) {
              locationCounts.states.set(state, (locationCounts.states.get(state) || 0) + 1);
              if (!locationCounts.cities.has(state)) {
                locationCounts.cities.set(state, new Map());
              }
              const stateCities = locationCounts.cities.get(state);
              stateCities.set(city, (stateCities.get(city) || 0) + 1);
              locationFound = true;
            }
          }
        }

        if (!ageFound) {
          const ageMatch = message.content.match(ageRegex);
          if (ageMatch) {
            const age = parseInt(ageMatch[1], 10);
            ageCounts.set(age, (ageCounts.get(age) || 0) + 1);
            ageFound = true;
          }
        }

        if (locationFound && ageFound) break;
      }

      if (locationFound || ageFound) {
        processedConvIds.add(conv.id);
      }
    });

    const sortedStates = Array.from(locationCounts.states.entries()).sort((a, b) => b[1] - a[1]);
    const sortedAges = Array.from(ageCounts.entries()).sort((a, b) => a[0] - b[0]);

    return {
      states: sortedStates,
      cities: locationCounts.cities,
      ages: sortedAges,
    };
  }, [conversations]);

  // Remove handleStateClick since we'll use dropdown to select state

  const stateChartData = useMemo(() => ({
    labels: demoData.states.map(s => s[0]),
    datasets: [{
      label: 'Conversaciones por Estado',
      data: demoData.states.map(s => s[1]),
      backgroundColor: 'rgba(54, 162, 235, 0.8)',
      borderColor: 'rgba(54, 162, 235, 1)',
      borderWidth: 1
    }]
  }), [demoData.states]);

  // City chart data for dropdown-selected state
  const cityChartData = useMemo(() => {
    if (!selectedMunicipalityState || !demoData.cities.has(selectedMunicipalityState)) return null;
    const cities = Array.from(demoData.cities.get(selectedMunicipalityState).entries()).sort((a, b) => b[1] - a[1]);
    return {
      labels: cities.map(c => c[0]),
      datasets: [{
        label: `Conversaciones por Municipio en ${selectedMunicipalityState}`,
        data: cities.map(c => c[1]),
        backgroundColor: 'rgba(75, 192, 192, 0.8)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1
      }]
    };
  }, [selectedMunicipalityState, demoData.cities]);

  const ageChartData = useMemo(() => ({
    labels: demoData.ages.map(a => `${a[0]} años`),
    datasets: [{
      label: 'Distribución de Edades',
      data: demoData.ages.map(a => a[1]),
      backgroundColor: 'rgba(255, 159, 64, 0.8)',
      borderColor: 'rgba(255, 159, 64, 1)',
      borderWidth: 1
    }]
  }), [demoData.ages]);

  const demographicChartOptions = (title) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { display: true, text: title, font: { size: 16, weight: 'bold' } },
      tooltip: {
        callbacks: {
          afterLabel: function(context) {
            if (title === 'Distribución por Estado') {
              const stateName = context.label;
              const citiesMap = demoData.cities.get(stateName);
              if (citiesMap && citiesMap.size > 0) {
                const cityList = Array.from(citiesMap.entries())
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 5)
                  .map(([city, count]) => `  - ${city}: ${count}`);
                return ['\nPrincipales Municipios:', ...cityList];
              }
            }
            return '';
          }
        }
      }
    },
    scales: {
      y: { beginAtZero: true, ticks: { stepSize: 1 } },
      x: { ticks: { maxRotation: 45, minRotation: 45, font: { size: 12 } } }
    }
  });

  const stateChartOptions = useMemo(() => demographicChartOptions('Distribución por Estado'), []);
  const cityChartOptions = useMemo(() => demographicChartOptions(`Distribución por Municipio en ${selectedMunicipalityState}`), [selectedMunicipalityState]);
  const ageChartOptions = useMemo(() => demographicChartOptions('Distribución de Edades'), []);

  const downloadDemographicAnalysis = useCallback(() => {
  const wb = utils.book_new();

  // States sheet
  const statesData = demoData.states.map(([state, count]) => ({ Estado: state, Conversaciones: count }));
  const statesWs = utils.json_to_sheet(statesData);
  utils.book_append_sheet(wb, statesWs, 'Estados');

  // Ages sheet
  const agesData = demoData.ages.map(([age, count]) => ({ Edad: age, Conversaciones: count }));
  const agesWs = utils.json_to_sheet(agesData);
  utils.book_append_sheet(wb, agesWs, 'Edades');

  // Municipios sheet - flatten cities data for all states
  const municipiosData = [];
  demoData.cities.forEach((citiesMap, state) => {
    citiesMap.forEach((count, city) => {
      municipiosData.push({ Estado: state, Municipio: city, Conversaciones: count });
    });
  });
  const municipiosWs = utils.json_to_sheet(municipiosData);
  utils.book_append_sheet(wb, municipiosWs, 'Municipios');

  const downloadDate = new Date().toISOString().slice(0, 10);
  writeFile(wb, `Analisis_Demografico_descargado_${downloadDate}.xlsx`);
}, [demoData]);


  return (
    <details className="mb-6 group">
      <summary className="text-xl font-bold text-gray-800 cursor-pointer list-none flex items-center">
        🌍 Análisis Demográfico
        <span className="ml-2 text-gray-500 transform transition-transform group-open:rotate-90">&#9654;</span>
      </summary>

      <div className="flex justify-end mt-4">
        <button onClick={downloadDemographicAnalysis} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm">
          📥 Descargar Excel
        </button>
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white border border-gray-300 rounded-lg p-4">
          <div style={{ height: '400px' }}>
            <Bar data={stateChartData} options={stateChartOptions} />
          </div>
          <p className="text-center text-sm text-gray-500 mt-2">Distribución por Estados</p>
        </div>

        <div className="bg-white border border-gray-300 rounded-lg p-4">
          <label htmlFor="stateSelect" className="block font-medium mb-2">Selecciona un Estado:</label>
          <select
            id="stateSelect"
            className="mb-4 p-2 border rounded w-full"
            value={selectedMunicipalityState}
            onChange={(e) => setSelectedMunicipalityState(e.target.value)}
          >
            <option value="">--Seleccione un Estado--</option>
            {demoData.states.map(([state]) => (
              <option key={state} value={state}>{state}</option>
            ))}
          </select>

          {selectedMunicipalityState && cityChartData ? (
            <div style={{ height: '400px' }}>
              <Bar data={cityChartData} options={cityChartOptions} />
            </div>
          ) : (
            <p className="text-gray-500 text-center">Por favor, selecciona un Estado para ver la distribución por Municipio.</p>
          )}
        </div>

        <div className="bg-white border border-gray-300 rounded-lg p-4 lg:col-span-2">
          <div style={{ height: '400px' }}>
            <Bar data={ageChartData} options={ageChartOptions} />
          </div>
          <p className="text-center text-sm text-gray-500 mt-2">Distribución por Edades</p>
        </div>
      </div>
    </details>
  );
};

export default DemographicAnalysis;
